import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react"
import { uploadRefusalMessage } from "@/lib/quotaGate"
import { useNavigate } from "react-router-dom"
import { attachThread, createThread, getCase, startReading, uploadDocuments } from "../api"
import type { QuotaMetric } from "@/lib/quotaGate"
import { useReaderContext } from "../readerContext"
import { GateLine, PaywallDoor, gateAction, useIsPro } from "../ds/gate"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { AttachIcon, SendIcon } from "../answer/icons"
import { track } from "@/lib/analytics"

/**
 * The hydration hook (handoff 3 §8, §13): the documents that would change a reading are asked for
 * where the reading names them, and a reading can be asked about and run again. Adding a document
 * is free and lands in the case file; reading the notice again with it is Nira Pro.
 */

const ACCEPT = ".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt,.md"

type Kind = "notice_no_reply" | "order_preview"
const METRIC: Record<Kind, QuotaMetric> = { notice_no_reply: "nira.notice_reading", order_preview: "nira.appeal_reading" }

/** The case file's documents in, and the reading out again with them. */
export function useHydrate(caseId: string, kind: Kind) {
  const navigate = useNavigate()
  const [added, setAdded] = useState(0)
  const [busy, setBusy] = useState<"" | "attach" | "rerun">("")
  const [said, setSaid] = useState("")
  // What the gate said when it stopped a re-run with nothing to sell (Pro's daily limit), or after "Not now".
  const [quiet, setQuiet] = useState("")
  const thread = useRef<string | null>(null)

  // Documents go into one of the case file's threads (a new one if it has none), so they land in it.
  const threadOf = async (): Promise<string> => {
    if (thread.current) return thread.current
    const detail = await getCase(caseId)
    const latest = [...detail.threads].sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0]
    if (latest) return (thread.current = latest.id)
    const row = await createThread(undefined, useReaderContext.getState().preferred, undefined, { caseId })
    await attachThread(caseId, row.id)
    return (thread.current = row.id)
  }

  const attach = async (files: FileList | null, what: string) => {
    if (!files?.length) return
    setBusy("attach")
    setSaid("")
    try {
      const rows = await uploadDocuments(await threadOf(), Array.from(files))
      setAdded((n) => n + rows.length)
      track("document_attached", { from: what ? "row" : "composer", kind: kind === "order_preview" ? "appeal" : "notice" })
    } catch (err) {
      setSaid(uploadRefusalMessage(err) ?? "Nira could not add that file. Try again.")
    } finally {
      setBusy("")
    }
  }

  const rerun = async () => {
    setSaid("")
    setQuiet("")
    // The reading again with documents is Pro (10 a day): asked on the press, before the reading runs.
    const gate = await gateAction("nira.rerun_documents", { trigger: 7, zeroIfUnlisted: true })
    if (!gate.ok) { setQuiet(gate.line); return }
    setBusy("rerun")
    try {
      const next = await startReading(caseId, kind, { with_documents: true })
      track("reading_rerun", { kind: kind === "order_preview" ? "appeal" : "notice", documents: added })
      navigate(`/case/${encodeURIComponent(caseId)}/reading/${encodeURIComponent(next.id)}`)
    } catch (err) {
      // A 402 has opened the paywall already; anything else is said here.
      if ((err as { status?: number })?.status !== 402) setSaid("The reading could not start again. Try again.")
    } finally {
      setBusy("")
    }
  }

  // Questions on a reading are research in the case file: a thread of its own, reading its documents.
  const ask = async (question: string) => {
    const row = await createThread(undefined, useReaderContext.getState().preferred, undefined, { caseId })
    await attachThread(caseId, row.id)
    track("reading_question_asked", { kind: kind === "order_preview" ? "appeal" : "notice" })
    navigate(`/nira/${encodeURIComponent(row.id)}`, { state: { question } })
  }

  return { added, busy, said, quiet, attach, rerun, ask, metric: METRIC[kind] }
}

export type Hydrate = ReturnType<typeof useHydrate>

/** "Attach the e-way bills": the file picker, with the document the row names as its label. */
export function AttachButton({ label, hydrate, what = label, badge }: { label: string; hydrate: Hydrate; what?: string; badge?: ReactNode }) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <button type="button" className="nr-attach" disabled={hydrate.busy === "attach"} onClick={() => input.current?.click()}>
        <AttachIcon />{label}{badge}
      </button>
      <input ref={input} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { void hydrate.attach(e.target.files, what); e.target.value = "" }} />
    </>
  )
}

/** "Attach the e-way bills for the invoices in the table": the row's own words, as an action. */
export const attachLabel = (what: string) => `Attach ${what.charAt(0).toLowerCase()}${what.slice(1)}`

/** Re-run with what was added. Pressing it asks the gate first (Pro, or the day's limit); nothing is locked on the page. */
export function RerunButton({ hydrate, className = "ncf-btn primary", children = "Re-run the reading" }: { hydrate: Hydrate; className?: string; children?: ReactNode }) {
  return (
    <button type="button" className={className} disabled={hydrate.busy === "rerun"} onClick={() => void hydrate.rerun()}>
      {hydrate.busy === "rerun" ? "Starting…" : children}
    </button>
  )
}

/** Once a document is added: one settled line at the top of the reading, and its one action. */
export function AddedBanner({ hydrate }: { hydrate: Hydrate }) {
  if (!hydrate.added && !hydrate.said && !hydrate.quiet) return null
  return (
    <div className="nrd-added" role="status">
      {hydrate.added ? <span>{hydrate.added} {hydrate.added === 1 ? "document" : "documents"} added</span> : null}
      {hydrate.added ? <RerunButton hydrate={hydrate} className="pro-link nrd-added-act" /> : null}
      {hydrate.said ? <span className="nrd-added-err">{hydrate.said}</span> : null}
      <GateLine text={hydrate.quiet} />
    </div>
  )
}

/**
 * The follow-up composer on every reading (handoff 3 §13), with the reading's own chips: attach a
 * document, run it again, and draft the reply paragraph for the first allegation (Pro).
 */
export function ReadingComposer({ hydrate, draftFor }: { hydrate: Hydrate; draftFor?: { id: string; title: string } }) {
  const [text, setText] = useState("")
  const [sending, setSending] = useState(false)
  const ta = useRef<HTMLTextAreaElement>(null)
  // The reply paragraph is Pro's: Pro (or no plan check at all, as locally) fills the chip in; anyone else meets the paywall on the press.
  const pro = useIsPro()
  const send = async () => {
    const q = text.trim()
    if (!q || sending) return
    setSending(true)
    try {
      await hydrate.ask(q)
    } catch {
      setSending(false)
    }
  }
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void send()
    }
  }
  const draft = draftFor ? `Draft the reply paragraph for ${draftFor.id}` : ""
  // The box is one line and grows with what is typed (or filled in); the CSS caps it.
  useEffect(() => {
    const el = ta.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }, [text])
  return (
    <section className="nrd-composer" aria-label="Ask about this reading">
      <div className="nrd-composer-in">
        <div className="sugs" aria-label="On this reading">
          <AttachButton label="Attach a document" what="" hydrate={hydrate} />
          <RerunButton hydrate={hydrate} className="sug">Re-run</RerunButton>
          <AllowanceHint metric="nira.rerun_documents" />
          {draftFor ? (
            pro ? (
              <button type="button" className="sug" onClick={() => { setText(`${draft} (${draftFor.title}).`); ta.current?.focus() }}>{draft}</button>
            ) : (
              <PaywallDoor className="sug" metric={hydrate.metric} trigger={11} source="reply_draft">
                {draft}
              </PaywallDoor>
            )
          ) : null}
        </div>
        <div className="dbox">
          <textarea ref={ta} rows={1} aria-label="Ask about this reading" placeholder="Ask about this reading…" value={text} onKeyDown={onKey}
            onChange={(e) => setText(e.target.value)} />
          <button className="send" type="button" aria-label="Ask" disabled={sending || !text.trim()} onClick={() => void send()}><SendIcon /></button>
        </div>
      </div>
    </section>
  )
}
